import { useEffect, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { LANGUAGES, previewPrompt, Prompt, stopPreview, toPhoneWav } from '../../lib/ivr';
import { Button, duration, Icon } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, useLoad, when } from './kit';

/** The recordings IVR flows play: greetings, menus, closed messages. */
export function Prompts() {
  const [prompts, reload] = useLoad<Prompt[]>('/prompts');
  const [adding, setAdding] = useState(false);
  const [playing, setPlaying] = useState<string | null>(null);
  useEffect(() => stopPreview, []);

  async function toggle(id: string) {
    try {
      const started = await previewPrompt(id, () => setPlaying(null));
      setPlaying(started ? id : null);
    } catch (err) {
      setPlaying(null);
      await act(() => Promise.reject(err));
    }
  }

  return (
    <>
      <SectionHeader
        title="Messages"
        lead="Recordings your IVR flows play to callers. Upload MP3, WAV or any audio your browser plays; it is converted to phone quality before upload."
        action={<AddButton onClick={() => setAdding(true)}>Upload message</AddButton>}
      />
      <Table head={['', 'Name', 'Language', 'Length', 'Used in', 'Added', '']} empty={prompts?.length === 0 && 'No messages yet. Upload a greeting to use in an IVR flow.'}>
        {(prompts ?? []).map((p) => (
          <Row key={p.id}>
            <Cell className="w-12">
              <button
                type="button"
                aria-label={playing === p.id ? `Stop ${p.name}` : `Play ${p.name}`}
                onClick={() => void toggle(p.id)}
                className={`grid h-8 w-8 place-items-center rounded-lg border ${playing === p.id ? 'border-ion bg-ion/15 text-ion' : 'border-line text-ink-2 hover:text-ink'}`}
              >
                <Icon name={playing === p.id ? 'pause' : 'play'} size={14} />
              </button>
            </Cell>
            <Cell className="font-medium">{p.name}</Cell>
            <Cell>{LANGUAGES[p.language]}</Cell>
            <Cell className="tabular">{duration(Math.round(p.duration_ms / 1000))}</Cell>
            <Cell>{p.used_in.length ? p.used_in.map((f) => <Pill key={f}>{f}</Pill>) : <span className="text-ink-3">Not used</span>}</Cell>
            <Cell className="text-ink-2">{when(p.created_at)}</Cell>
            <Cell className="text-right">
              <ConfirmButton
                confirmText="Delete message"
                onConfirm={async () => {
                  const ok = await act(() => api('DELETE', `/prompts/${p.id}`).then(() => true), `${p.name} deleted`);
                  if (ok) reload();
                }}
              >
                Delete
              </ConfirmButton>
            </Cell>
          </Row>
        ))}
      </Table>
      {adding ? (
        <UploadDrawer
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function UploadDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<Prompt['language']>('en');
  const [file, setFile] = useState<File | null>(null);
  const [converted, setConverted] = useState<{ wav: Blob; seconds: number; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => (converted ? URL.revokeObjectURL(converted.url) : undefined), [converted]);

  async function pick(f: File | null) {
    setFile(f);
    setConverted(null);
    setError(null);
    if (!f) return;
    if (!name) setName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 60));
    try {
      const out = await toPhoneWav(f);
      if (out.seconds > 300) throw new Error('Messages can be up to 5 minutes long.');
      if (out.seconds < 0.2) throw new Error('This recording is too short.');
      setConverted({ ...out, url: URL.createObjectURL(out.wav) });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function save() {
    if (!converted) return;
    setBusy(true);
    const q = new URLSearchParams({ name: name.trim(), language });
    const ok = await act(() => api('POST', `/prompts?${q}`, converted.wav), `${name.trim()} uploaded`);
    setBusy(false);
    if (ok) onDone();
  }

  return (
    <Drawer
      title="Upload a message"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={busy || !converted || !name.trim()}>
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Audio file" hint="MP3, WAV, M4A or OGG, up to 5 minutes.">
        {(id) => <input id={id} type="file" accept="audio/*" className={`${inputClass} file:mr-3 file:rounded-md file:border-0 file:bg-hull-2 file:px-2 file:py-1 file:text-ink`} onChange={(e) => void pick(e.target.files?.[0] ?? null)} />}
      </Field>
      {error ? <p className="-mt-2 mb-4 text-sm text-break">{error}</p> : null}
      {converted ? (
        <div className="mb-4 rounded-xl border border-line p-3">
          <p className="mb-2 text-xs text-ink-3">How callers will hear it ({duration(Math.round(converted.seconds))})</p>
          <audio controls src={converted.url} className="w-full" />
        </div>
      ) : file && !error ? (
        <p className="mb-4 text-sm text-ink-2">Converting…</p>
      ) : null}
      <Field label="Name" hint="For example: Welcome, Main menu, Closed for the day.">
        {(id) => <input id={id} className={inputClass} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Language">
        {(id) => (
          <select id={id} className={inputClass} value={language} onChange={(e) => setLanguage(e.target.value as Prompt['language'])}>
            {Object.entries(LANGUAGES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        )}
      </Field>
    </Drawer>
  );
}
